import { CloseOutlined, SaveFilled } from "@ant-design/icons"
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Flex,
  Form,
  type FormInstance,
  Space,
  Switch,
  Tooltip,
  Typography
} from "antd"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  type DesktopNotificationPermission,
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission
} from "../../../utils/notification/browser-notification.ts"
import { subscribeToWebPush } from "../../../utils/notification/web-push.ts"
import { FormChangedWatcher } from "../../../components/Forms/FormChangedWatcher/FormChangedWatcher"
import { MAIN_CONTENT_STICKY_BOTTOM_CLASS } from "../../../components/main-content"
import {
  CHANNEL_KEYS,
  CRITICAL_GROUPS,
  OPTIONAL_CHANNEL_KEYS
} from "../constants/notifications-settings"
import { type SmsLock } from "../sms/use-sms-verification"
import type {
  NotificationSettings,
  NotificationSettingsItem
} from "../types/notification.types"
import { anyDesktopOn } from "../helpers/desktopPreference.ts"
import { NotificationGroupIcon } from "./NotificationGroupIcon"

const { Text } = Typography

type Channel = "email" | "in_app" | "phone" | "desktop" | "whatsapp" | "sms"
type ChannelValues = Partial<Record<Channel, boolean>>

const CHANNELS = [...CHANNEL_KEYS, ...OPTIONAL_CHANNEL_KEYS] as Channel[]

// When a bulk "off" would empty a critical row, one channel is kept on. This is
// the order it prefers among the channels currently enabled on that row.
const KEEP_PRIORITY: Channel[] = [
  "in_app",
  "email",
  "phone",
  "desktop",
  "whatsapp",
  "sms"
]

// The channels this row can carry. A row only gets a switch for a channel the
// server sent, so a payload from before WhatsApp or SMS saves exactly as before,
// and SMS only where the server says the type may go by text.
function rowChannels(item: NotificationSettingsItem): Channel[] {
  return CHANNELS.filter((c) => {
    if (c === "sms") return item.sms_available === true
    if (c === "whatsapp") return typeof item.whatsapp === "boolean"
    return true
  })
}

// A locked SMS switch keeps the stored choice; bulk switches pass over it.
function editableChannels(
  item: NotificationSettingsItem,
  smsLock: SmsLock
): Channel[] {
  return rowChannels(item).filter((c) => c !== "sms" || !smsLock)
}

function groupChannels(items: NotificationSettingsItem[]): Channel[] {
  return CHANNELS.filter((c) => items.some((it) => rowChannels(it).includes(c)))
}

function enabledCount(row: ChannelValues, channels: Channel[]) {
  return channels.filter((c) => row[c]).length
}

// Value a single channel takes when a bulk "off" sweeps it: false, unless that
// would leave a critical row with nothing on — then it stays.
function clearedChannel(
  row: ChannelValues,
  channel: Channel,
  channels: Channel[],
  isCriticalGroup: boolean
): boolean {
  if (isCriticalGroup && row[channel] && enabledCount(row, channels) === 1)
    return true
  return false
}

// Whole-row value after a bulk "off". A critical row keeps its highest-priority
// still-enabled channel so finance alerts never go fully dark.
function clearedRow(
  row: ChannelValues,
  channels: Channel[],
  isCriticalGroup: boolean
): ChannelValues {
  if (!isCriticalGroup || enabledCount(row, channels) === 0) {
    return Object.fromEntries(channels.map((c) => [c, false])) as ChannelValues
  }
  const keep =
    KEEP_PRIORITY.find((c) => channels.includes(c) && row[c]) ?? "in_app"
  return Object.fromEntries(
    channels.map((c) => [c, c === keep])
  ) as ChannelValues
}

function useGroupRows(
  items: NotificationSettingsItem[],
  form: FormInstance
): Record<number, ChannelValues> {
  const watched = Form.useWatch([], form) as
    | Record<number, Partial<ChannelValues>>
    | undefined

  return Object.fromEntries(
    items.map((item) => {
      const w = watched?.[item.notification_type]
      return [
        item.notification_type,
        Object.fromEntries(
          rowChannels(item).map((c) => [c, w?.[c] ?? item[c]])
        ) as ChannelValues
      ]
    })
  )
}

interface BulkSwitchProps {
  groupKey: string
  items: NotificationSettingsItem[]
  isCriticalGroup: boolean
  form: FormInstance
  smsLock: SmsLock
}

function SectionMasterSwitch({
  groupKey,
  items,
  isCriticalGroup,
  form,
  smsLock
}: BulkSwitchProps) {
  const { t } = useTranslation()
  const rows = useGroupRows(items, form)

  const allOn = items.every((it) =>
    editableChannels(it, smsLock).every((c) => rows[it.notification_type][c])
  )
  const someOn = items.some((it) =>
    editableChannels(it, smsLock).some((c) => rows[it.notification_type][c])
  )
  const partiallyOn = someOn && !allOn

  const handleChange = (next: boolean) => {
    const patch: Record<number, ChannelValues> = {}
    items.forEach((it) => {
      const channels = editableChannels(it, smsLock)
      patch[it.notification_type] = next
        ? (Object.fromEntries(channels.map((c) => [c, true])) as ChannelValues)
        : clearedRow(rows[it.notification_type], channels, isCriticalGroup)
    })
    form.setFieldsValue(patch)
  }

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Tooltip
        title={
          partiallyOn
            ? t("notifications.settings.sectionPartiallyOn")
            : undefined
        }
      >
        <Switch
          size="small"
          checked={allOn}
          onChange={handleChange}
          className={partiallyOn ? "opacity-60" : undefined}
          data-testid={`bulk-toggle-${groupKey}-all`}
          aria-label={t("notifications.settings.toggleSection", {
            group: t(`notifications.groups.${groupKey}`, {
              defaultValue: groupKey
            })
          })}
        />
      </Tooltip>
    </span>
  )
}

function ChannelColumnSwitch({
  groupKey,
  channel,
  items,
  isCriticalGroup,
  form,
  smsLock
}: BulkSwitchProps & { channel: Channel }) {
  const { t } = useTranslation()
  const rows = useGroupRows(items, form)
  const carriers = items.filter((it) => rowChannels(it).includes(channel))
  const values = carriers.map((it) => rows[it.notification_type])

  const allOn = values.every((r) => r[channel])
  const someOn = values.some((r) => r[channel])
  const partiallyOn = someOn && !allOn
  const locked = channel === "sms" && smsLock !== null

  const handleChange = (next: boolean) => {
    const patch: Record<number, ChannelValues> = {}
    carriers.forEach((it) => {
      const row = rows[it.notification_type]
      patch[it.notification_type] = {
        ...row,
        [channel]: next
          ? true
          : clearedChannel(row, channel, rowChannels(it), isCriticalGroup)
      }
    })
    form.setFieldsValue(patch)
  }

  return (
    <Tooltip
      title={
        locked
          ? t(`smsVerification.locked.${smsLock}`)
          : partiallyOn
            ? t("notifications.settings.sectionPartiallyOn")
            : undefined
      }
    >
      <Switch
        size="small"
        checked={allOn}
        disabled={locked}
        onChange={handleChange}
        className={partiallyOn ? "opacity-60" : undefined}
        data-testid={`bulk-toggle-${groupKey}-${channel}`}
        aria-label={t("notifications.settings.toggleChannel", {
          channel: t(`notifications.channels.${channel}`),
          group: t(`notifications.groups.${groupKey}`, {
            defaultValue: groupKey
          })
        })}
      />
    </Tooltip>
  )
}

interface NotificationSettingRowProps {
  item: NotificationSettingsItem
  columns: Channel[]
  isCriticalGroup: boolean
  form: FormInstance
  smsLock: SmsLock
}

function NotificationSettingRow({
  item,
  columns,
  isCriticalGroup,
  form,
  smsLock
}: NotificationSettingRowProps) {
  const { t } = useTranslation()
  const row = Form.useWatch([item.notification_type], form) ?? item
  const channels = rowChannels(item)
  const enabled = enabledCount(row, channels)

  return (
    <div
      className={"border-b border-border grid py-1.5 px-4 items-center"}
      style={{ gridTemplateColumns: `2fr repeat(${columns.length}, 1fr)` }}
    >
      <Text>{item.notification_type_name}</Text>
      {columns.map((channel) => {
        if (!channels.includes(channel)) {
          return (
            <span
              key={channel}
              data-testid={`no-${channel}-${item.notification_type}`}
            />
          )
        }
        const locked = isCriticalGroup && row[channel] === true && enabled === 1
        const smsLocked = channel === "sms" && smsLock !== null

        return (
          <Flex justify="center" key={channel}>
            <Tooltip
              title={
                smsLocked
                  ? t(`smsVerification.locked.${smsLock}`)
                  : locked
                    ? t("notifications.settings.criticalLocked")
                    : undefined
              }
            >
              <span>
                <Form.Item
                  name={[item.notification_type, channel]}
                  valuePropName="checked"
                  initialValue={item[channel]}
                  noStyle
                >
                  <Switch
                    size="small"
                    disabled={locked || smsLocked}
                    aria-label={t("notifications.settings.toggleRowChannel", {
                      channel: t(`notifications.channels.${channel}`),
                      type: item.notification_type_name
                    })}
                  />
                </Form.Item>
              </span>
            </Tooltip>
          </Flex>
        )
      })}
    </div>
  )
}

interface NotificationSettingsFormProps {
  settings: NotificationSettings
  onFinish: (values: { [key: string]: NotificationSettingsItem }) => void
  saving: boolean
  smsLock?: SmsLock
}

export function NotificationSettingsForm({
  settings,
  onFinish,
  saving,
  smsLock = null
}: NotificationSettingsFormProps) {
  const [form] = Form.useForm()
  const { t } = useTranslation()

  const [isDirty, setIsDirty] = useState(false)
  const [desktopPermission, setDesktopPermission] =
    useState<DesktopNotificationPermission>(getDesktopNotificationPermission)

  // Turning the Desktop channel on is the explicit action the browser requires
  // before it will prompt — asking on page load would be refused, and asking
  // repeatedly is what gets a site permanently blocked. Once the answer is
  // "denied" or "unsupported" the browser will not ask again, so we stop
  // asking and explain instead.
  const handleValuesChange = (
    changed: Record<string, Partial<Record<string, boolean>>>
  ) => {
    const turnedDesktopOn = Object.values(changed).some(
      (row) => row && row.desktop === true
    )
    if (!turnedDesktopOn) return
    if (getDesktopNotificationPermission() !== "default") {
      const permission = getDesktopNotificationPermission()
      setDesktopPermission(permission)
      if (permission === "granted") void subscribeToWebPush()
      return
    }
    void askForDesktopPermission()
  }

  // A Desktop switch delivers nothing to this browser until it holds a push
  // subscription, and permission alone never creates one. Asking needs a click,
  // so a switch that is on by default waits for "Allow" below.
  const askForDesktopPermission = async () => {
    const permission = await requestDesktopNotificationPermission()
    setDesktopPermission(permission)
    if (permission === "granted") void subscribeToWebPush()
  }

  const desktopWanted = anyDesktopOn(settings)

  useEffect(() => {
    if (desktopWanted && getDesktopNotificationPermission() === "granted") {
      void subscribeToWebPush()
    }
  }, [desktopWanted])

  const desktopBlocked =
    desktopPermission === "denied" || desktopPermission === "unsupported"
  const desktopNotYetAllowed = desktopWanted && desktopPermission === "default"

  // A later answer from the server (the SMS defaults a first verification
  // switches on) replaces what the user has not touched, and only that.
  useEffect(() => {
    if (!settings) return
    const untouched = Object.values(settings)
      .flat()
      .flatMap((item) =>
        rowChannels(item)
          .map((channel) => ({
            name: [item.notification_type, channel],
            value: item[channel]
          }))
          .filter(({ name }) => !form.isFieldTouched(name))
      )
    form.setFields(untouched)
  }, [settings, form])

  return (
    <div className={"flex-1 flex gap-3 flex-col w-full with-default-error"}>
      {desktopBlocked && (
        <Alert
          type={"warning"}
          showIcon
          message={
            desktopPermission === "unsupported"
              ? t(
                  "notifications.settings.desktopUnsupported",
                  "This browser does not support desktop notifications."
                )
              : t(
                  "notifications.settings.desktopBlocked",
                  "Desktop notifications are blocked in this browser. Allow them in your browser's site settings to receive desktop alerts. Everything else still arrives in the app."
                )
          }
        />
      )}
      {desktopNotYetAllowed && (
        <Alert
          type={"info"}
          showIcon
          message={t(
            "notifications.settings.desktopNotYetAllowed",
            "Desktop alerts are on for some notifications, but this browser has not been allowed to show them yet."
          )}
          action={
            <Button
              size={"small"}
              onClick={() => void askForDesktopPermission()}
            >
              {t("notifications.settings.allowDesktop", "Allow")}
            </Button>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        component={false}
        onFinish={onFinish}
        onValuesChange={handleValuesChange}
        disabled={saving}
      >
        <FormChangedWatcher
          onDirtyChange={setIsDirty}
          form={form}
          saving={saving}
        />
        <div className={"flex-1"}>
          {settings ? (
            <Collapse
              className="border-none bg-transparent"
              defaultActiveKey={Object.keys(settings)}
              items={Object.entries(settings).map(([key, value]) => {
                const isCriticalGroup = CRITICAL_GROUPS.includes(key)
                const columns = groupChannels(value)

                return {
                  className:
                    "rounded-md border border-border shadow-sm overflow-hidden mb-2 h-fit",
                  key: key,
                  label: (
                    <Flex align="center" gap={8}>
                      <NotificationGroupIcon groupKey={key} />
                      <Text strong>
                        {t(`notifications.groups.${key}`, {
                          defaultValue: key
                        })}
                      </Text>
                      <SectionMasterSwitch
                        groupKey={key}
                        items={value}
                        isCriticalGroup={isCriticalGroup}
                        form={form}
                        smsLock={smsLock}
                      />
                    </Flex>
                  ),
                  children: (
                    <div>
                      <div
                        className={
                          "py-1.5 px-4 grid border-b border-border bg-background"
                        }
                        style={{
                          gridTemplateColumns: `2fr repeat(${columns.length}, 1fr)`
                        }}
                      >
                        <div />
                        {columns.map((channel) => (
                          <div
                            key={channel}
                            className={"flex items-center justify-center"}
                          >
                            <span
                              className={"relative inline-flex items-center"}
                            >
                              <Text
                                type="secondary"
                                className={
                                  "absolute right-full mr-2 text-xs whitespace-nowrap"
                                }
                              >
                                {channel === "in_app" ? (
                                  <Tooltip
                                    title={t(
                                      "notifications.settings.inAppHint"
                                    )}
                                  >
                                    <span className="underline decoration-dotted cursor-help">
                                      {t(`notifications.channels.${channel}`)}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  t(`notifications.channels.${channel}`)
                                )}
                              </Text>
                              <ChannelColumnSwitch
                                groupKey={key}
                                channel={channel}
                                items={value}
                                isCriticalGroup={isCriticalGroup}
                                form={form}
                                smsLock={smsLock}
                              />
                            </span>
                          </div>
                        ))}
                      </div>
                      {value.map((item) => (
                        <NotificationSettingRow
                          key={item.notification_type}
                          item={item}
                          columns={columns}
                          isCriticalGroup={isCriticalGroup}
                          form={form}
                          smsLock={smsLock}
                        />
                      ))}
                    </div>
                  )
                }
              })}
            />
          ) : (
            <Empty description={t("notifications.settings.noSettings")} />
          )}
        </div>
        <Flex
          justify="end"
          className={`${MAIN_CONTENT_STICKY_BOTTOM_CLASS} z-[2] bg-background py-2`}
        >
          <Space>
            <Button
              onClick={() => form.resetFields()}
              disabled={!isDirty || saving}
              icon={<CloseOutlined />}
              variant={"solid"}
            >
              {t("notifications.settings.cancelChanges")}
            </Button>
            <Button
              disabled={!isDirty || saving}
              loading={saving}
              onClick={form.submit}
              icon={<SaveFilled />}
              variant={"solid"}
              color={"primary"}
            >
              {t("notifications.settings.saveChanges")}
            </Button>
          </Space>
        </Flex>
      </Form>
    </div>
  )
}
